/**
 * AI Image Service - Shared service for AI image generation
 * Dùng chung cho AutoPostGenerator, NormalPostGenerator, AddListImagesScreen
 */

import apiClient from "../api/config";

// Image source constants
export const IMAGE_SOURCES = {
  INTERNET: "serper",
  AI: "gemini",
  UPLOAD: "upload",
  NONE: "none",
};

// Image source labels cho UI
export const IMAGE_SOURCE_LABELS = {
  [IMAGE_SOURCES.INTERNET]: "🌐 Ảnh từ Internet",
  [IMAGE_SOURCES.AI]: "🤖 Ảnh AI tạo tự động",
  [IMAGE_SOURCES.UPLOAD]: "📸 Ảnh tự tải lên",
  [IMAGE_SOURCES.NONE]: "❌ Không dùng ảnh",
};

// Upload image limits
export const UPLOAD_IMAGE_MAX_COUNT = 30;
export const UPLOAD_IMAGE_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
export const UPLOAD_IMAGE_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

export const DEFAULT_IMAGE_SOURCE = IMAGE_SOURCES.INTERNET;

// ===== IMAGE STYLES =====
export const IMAGE_STYLES = {
  REALISTIC: "realistic",
  ILLUSTRATION: "illustration",
  WATERCOLOR: "watercolor",
  CINEMATIC: "cinematic",
  MINIMALIST: "minimalist",
  CARTOON: "cartoon",
  THREE_D: "3d_render",
  VINTAGE: "vintage",
};

export const IMAGE_STYLE_OPTIONS = [
  { value: IMAGE_STYLES.REALISTIC, label: "📸 Chân thực", prompt: "Photorealistic photography, sharp details, natural colors" },
  { value: IMAGE_STYLES.ILLUSTRATION, label: "🎨 Minh họa", prompt: "Digital illustration, clean vector lines, vibrant flat colors" },
  { value: IMAGE_STYLES.WATERCOLOR, label: "🖌️ Màu nước", prompt: "Delicate watercolor painting, soft blending, organic edges" },
  { value: IMAGE_STYLES.CINEMATIC, label: "🎬 Điện ảnh", prompt: "Cinematic shot, dramatic lighting, shallow depth of field, film grain" },
  { value: IMAGE_STYLES.MINIMALIST, label: "◽ Tối giản", prompt: "Minimalist composition, ample white space, single focal element" },
  { value: IMAGE_STYLES.CARTOON, label: "🎭 Hoạt hình", prompt: "Colorful cartoon style, exaggerated proportions, bold outlines" },
  { value: IMAGE_STYLES.THREE_D, label: "🧊 3D Render", prompt: "3D rendered scene, smooth surfaces, studio lighting, soft shadows" },
  { value: IMAGE_STYLES.VINTAGE, label: "📻 Cổ điển", prompt: "Vintage aesthetic, warm sepia tones, faded edges, retro color grading" },
];

export const DEFAULT_IMAGE_STYLE = IMAGE_STYLES.REALISTIC;

// ===== ASPECT RATIOS =====
export const ASPECT_RATIOS = {
  SQUARE: "1:1",
  LANDSCAPE: "16:9",
  PORTRAIT: "4:5",
};

export const ASPECT_RATIO_OPTIONS = [
  { value: ASPECT_RATIOS.SQUARE, label: "⬜ Vuông", desc: "1:1 — Feed" },
  { value: ASPECT_RATIOS.LANDSCAPE, label: "🖥️ Ngang", desc: "16:9 — Cover" },
  { value: ASPECT_RATIOS.PORTRAIT, label: "📱 Dọc", desc: "4:5 — Story" },
];

export const DEFAULT_ASPECT_RATIO = ASPECT_RATIOS.SQUARE;

// ===== DEFAULT IMAGE SETTINGS =====
export const DEFAULT_IMAGE_SETTINGS = {
  imageStyle: DEFAULT_IMAGE_STYLE,
  aspectRatio: DEFAULT_ASPECT_RATIO,
  customImagePrompt: "",
};

// ===== WORDPRESS IMAGE COUNT =====
export const WP_IMAGE_COUNT_OPTIONS = [2, 3, 4, 5];
export const DEFAULT_WP_IMAGE_COUNT = 2;
export const WP_AI_IMAGE_COUNT = 2; // Cố định cho nguồn ảnh AI

// ===== FACEBOOK IMAGE COUNT (Upload source) =====
export const FB_IMAGE_COUNT_OPTIONS = [1, 2, 3, 4, 5];
export const DEFAULT_FB_IMAGE_COUNT = 1; // Backward compat: post cũ mặc định 1 ảnh

/**
 * Tạo ảnh AI bằng Gemini qua backend API
 */
export async function generateAIImage(prompt, options = {}) {
  const userId = localStorage.getItem("userId");

  if (!userId) {
    throw new Error("Chưa đăng nhập");
  }

  const imageSettings = options.imageSettings || options;
  const response = await apiClient.post(
    "/api/content/generate-image-with-gemini",
    {
      prompt,
      userId,
      imageSettings,
    },
    { timeout: options.timeoutMs || 15000 }
  );

  if (response.data.success) {
    return {
      success: true,
      imageUrl: response.data.data?.imageUrl || null,
      jobId: response.data.data?.jobId || null,
      estimatedTime: response.data.data?.estimatedTime,
    };
  }

  return { success: false, imageUrl: null, jobId: null };
}

export default {
  IMAGE_SOURCES,
  IMAGE_SOURCE_LABELS,
  DEFAULT_IMAGE_SOURCE,
  IMAGE_STYLES,
  IMAGE_STYLE_OPTIONS,
  DEFAULT_IMAGE_STYLE,
  ASPECT_RATIOS,
  ASPECT_RATIO_OPTIONS,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_IMAGE_SETTINGS,
  WP_IMAGE_COUNT_OPTIONS,
  DEFAULT_WP_IMAGE_COUNT,
  WP_AI_IMAGE_COUNT,
  UPLOAD_IMAGE_MAX_COUNT,
  UPLOAD_IMAGE_MAX_SIZE_BYTES,
  UPLOAD_IMAGE_ACCEPTED_TYPES,
  generateAIImage,
};
